<script setup>
import { computed, useAttrs } from 'vue'
import { useFormFieldContext } from '@/composables/useFormFieldContext'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue: { type: [String, Number, Boolean, Array], default: '' },
  id: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  invalid: { type: Boolean, default: null },
  density: { type: String, default: '' },
  multiple: { type: Boolean, default: false },
  /** Optional list of { value, label, disabled? }. Prefer slot for complex options. */
  options: { type: Array, default: null },
  ariaLabel: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue'])
const attrs = useAttrs()
const { resolveId, resolveDescribedby, resolveInvalid, resolveDensity } = useFormFieldContext()

const resolvedId = computed(() => resolveId(props.id))
const resolvedInvalid = computed(() => resolveInvalid(props.invalid))
const resolvedDensity = computed(() => resolveDensity(props.density))
const describedby = computed(() => resolveDescribedby(attrs['aria-describedby']))

const inputClass = computed(() => [
  'form-input',
  'form-select',
  props.multiple ? 'form-select--multiple' : '',
  resolvedDensity.value === 'compact' ? 'form-input--compact' : '',
  resolvedInvalid.value ? 'is-invalid' : '',
  attrs.class,
])

function onChange(e) {
  if (props.multiple) {
    const selected = Array.from(e.target.selectedOptions).map((o) => o.value)
    emit('update:modelValue', selected)
    return
  }
  emit('update:modelValue', e.target.value)
}
</script>

<template>
  <select
    :id="resolvedId"
    :value="modelValue"
    :disabled="disabled"
    :multiple="multiple"
    :class="inputClass"
    :aria-invalid="resolvedInvalid ? true : undefined"
    :aria-describedby="describedby"
    :aria-label="ariaLabel || undefined"
    v-bind="{ ...attrs, class: undefined }"
    @change="onChange"
  >
    <template v-if="options">
      <option
        v-for="opt in options"
        :key="String(opt.value)"
        :value="opt.value"
        :disabled="!!opt.disabled"
      >
        {{ opt.label }}
      </option>
    </template>
    <slot v-else />
  </select>
</template>
